import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Search as IcSearch, RefreshCw as IcRefresh, Send as IcSend, Mail as IcMail, Clock as IcClock, AlertCircle as IcAlert, Pause as IcPause, Building2 as IcCompany, User as IcUser, MailX as IcEmpty, Play as IcPlay, SkipForward as IcSkip, Trash2 as IcRemove, Zap as IcZap } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const PAGE_SIZE = 100;
const POLL_MS = 30000;

const STATUS_META = {
  pending:     { label: 'Pending',     colour: '#0e7490', bg: '#e0f2fe', Icon: IcClock },
  in_progress: { label: 'In progress', colour: '#a16207', bg: '#fef3c7', Icon: IcSend },
  paused:      { label: 'Paused',      colour: '#666',    bg: '#f4f4f5', Icon: IcPause },
  failed:      { label: 'Failed',      colour: '#b91c1c', bg: '#fee2e2', Icon: IcAlert },
};

function formatWhen(ts) {
  if (!ts) return 'unscheduled';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = d - now;
  const absMin = Math.abs(diffMs) / 60000;
  if (absMin < 60) return diffMs > 0 ? `in ${Math.round(absMin)}m` : `${Math.round(absMin)}m ago`;
  const absHr = absMin / 60;
  if (absHr < 24) return diffMs > 0 ? `in ${Math.round(absHr)}h` : `${Math.round(absHr)}h ago`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString([], { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFullWhen(ts) {
  if (!ts) return 'unscheduled';
  const d = new Date(ts);
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
         d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function MailOutbox() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [sequenceContent, setSequenceContent] = useState(null);
  const [sequenceLoading, setSequenceLoading] = useState(false);
  const [sequenceError, setSequenceError] = useState(null);
  const [campaignWaitDays, setCampaignWaitDays] = useState([2, 3, 4, 5]);

  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    if (campaignFilter) params.set('campaign_id', campaignFilter);
    return isGlobalMode
      ? `${API_BASE}/admin/outbox?${params.toString()}`
      : `${API_BASE}/clients/${clientId}/outbox?${params.toString()}`;
  }, [campaignFilter, isGlobalMode, clientId]);

  const fetchList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const res = await fetch(buildUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load outbox (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setList(rows);
      if (firstLoadRef.current && rows.length) {
        firstLoadRef.current = false;
        setSelectedId(rows[0].id);
      }
    } catch (e) {
      if (!silent) setListError(e.message);
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [buildUrl, getToken]);

  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    firstLoadRef.current = true;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, campaignFilter]);

  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    pollRef.current = setInterval(() => fetchList({ silent: true }), POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, campaignFilter]);

  useEffect(() => {
    let cancelled = false;
    const contactId = list.find(r => r.id === selectedId)?.contact_id;
    if (!contactId) { setSequenceContent(null); setSequenceError(null); return; }
    setSequenceLoading(true);
    setSequenceError(null);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`Failed to load sequence (${res.status})`);
        const rows = await res.json();
        if (cancelled) return;
        const emailRow = (Array.isArray(rows) ? rows : []).find(r => r.channel === 'email');
        setSequenceContent(emailRow || null);
        // Also fetch the campaign to get default_step_wait_days
        const selectedRow = list.find(r => r.id === selectedId);
        if (selectedRow?.campaign_id) {
          try {
            const cres = await fetch(`${API_BASE}/campaigns/${selectedRow.campaign_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (cres.ok) {
              const cdata = await cres.json();
              if (!cancelled) {
                setCampaignWaitDays(Array.isArray(cdata.default_step_wait_days) && cdata.default_step_wait_days.length ? cdata.default_step_wait_days : [2, 3, 4, 5]);
              }
            }
          } catch (_) {}
        }
      } catch (e) {
        if (!cancelled) setSequenceError(e.message);
      } finally {
        if (!cancelled) setSequenceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, list, getToken]);

  const campaigns = useMemo(() => {
    const m = new Map();
    list.forEach(r => { if (r.campaign_id && r.campaign_name) m.set(r.campaign_id, r.campaign_name); });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [list]);

  const filteredList = useMemo(() => {
    let out = list;
    if (statusFilter) out = out.filter(r => r.status === statusFilter);
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(r =>
        (r.full_name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.company_name || '').toLowerCase().includes(q) ||
        (r.campaign_name || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [list, query, statusFilter]);

  const selected = useMemo(() => filteredList.find(r => r.id === selectedId) || list.find(r => r.id === selectedId), [filteredList, list, selectedId]);

  const statusCounts = useMemo(() => {
    const counts = { pending: 0, in_progress: 0, paused: 0, failed: 0 };
    list.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [list]);

  const doAction = async (action) => {
    if (!selected) return;
    if (action === 'remove' && !window.confirm('Remove this contact from the campaign? This cannot be undone.')) return;
    setActionBusy(action);
    setActionError(null);
    try {
      const token = await getToken();
      const method = action === 'remove' ? 'DELETE' : 'POST';
      const path = action === 'remove'
        ? `/campaign-contacts/${selected.id}`
        : `/campaign-contacts/${selected.id}/${action}`;
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      if (action === 'remove') setSelectedId(null);
      await fetchList();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActionBusy(null);
    }
  };

  const actionBtnStyle = (colour, busy, outline) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', fontSize: 12, fontWeight: 500,
    borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
    background: outline ? '#fff' : colour,
    color: outline ? colour : '#fff',
    border: outline ? `1px solid ${colour}` : 'none',
    opacity: busy ? 0.6 : 1,
  });

  const projectedDates = useMemo(() => {
    if (!selected || !sequenceContent || !Array.isArray(sequenceContent.content)) return {};
    const steps = sequenceContent.content;
    const currentStepNum = selected.current_step != null ? (selected.current_step + 1) : 1;
    // If no next_send_at (e.g. paused or unscheduled), project from "now" as a hypothetical
    const anchorDate = selected.next_send_at ? new Date(selected.next_send_at) : new Date();
    const isHypothetical = !selected.next_send_at;
    const dates = { _hypothetical: isHypothetical };
    // The current step sends on next_send_at
    dates[currentStepNum] = anchorDate;
    // Forward project subsequent steps
    let cursor = new Date(anchorDate);
    for (let i = currentStepNum; i < steps.length; i++) {
      const stepData = steps[i - 1];
      const waitDays = (stepData && typeof stepData.wait_days === 'number') ? stepData.wait_days : (campaignWaitDays[i - 1] != null ? campaignWaitDays[i - 1] : 3);
      cursor = new Date(cursor.getTime() + waitDays * 24 * 60 * 60 * 1000);
      dates[i + 1] = new Date(cursor);
    }
    // Back project earlier steps (already sent)
    cursor = new Date(anchorDate);
    for (let i = currentStepNum - 1; i >= 1; i--) {
      const stepData = steps[i - 1];
      const waitDays = (stepData && typeof stepData.wait_days === 'number') ? stepData.wait_days : (campaignWaitDays[i - 1] != null ? campaignWaitDays[i - 1] : 3);
      cursor = new Date(cursor.getTime() - waitDays * 24 * 60 * 60 * 1000);
      dates[i] = new Date(cursor);
    }
    return dates;
  }, [selected, sequenceContent, campaignWaitDays]);

  const formatStepDate = (d, isPast, isCurrent) => {
    if (!d) return null;
    const opts = { day: 'numeric', month: 'short' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    const dateStr = d.toLocaleDateString([], opts);
    if (isCurrent) return `Sends ${dateStr}`;
    if (isPast) return `Sent ${dateStr}`;
    return `~ ${dateStr}`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 6, overflow: 'hidden' }}>

      {/* LEFT RAIL */}
      <div style={{ width: 380, borderRight: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
        <div style={{ padding: 14, borderBottom: '1px solid #e5e5e5', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Outbox</h2>
            <button onClick={() => fetchList()} title="Refresh"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#666' }}>
              <IcRefresh size={15} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_META).map(([key, meta]) => {
              const count = statusCounts[key];
              const active = statusFilter === key;
              return (
                <button key={key} onClick={() => setStatusFilter(active ? '' : key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', fontSize: 11, borderRadius: 3,
                    background: active ? meta.colour : meta.bg,
                    color: active ? '#fff' : meta.colour,
                    border: 'none', cursor: 'pointer', fontWeight: 500,
                  }}>
                  <meta.Icon size={10} />
                  {meta.label} {count > 0 && <span style={{ opacity: 0.8 }}>· {count}</span>}
                </button>
              );
            })}
          </div>

          <div style={{ position: 'relative', marginBottom: 8 }}>
            <IcSearch size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#999' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contact, email, company…"
              style={{ width: '100%', padding: '7px 10px 7px 32px', fontSize: 13, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff', boxSizing: 'border-box' }} />
          </div>
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff' }}>
            <option value="">All campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {listLoading && list.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>Loading…</div>
          )}
          {listError && (
            <div style={{ padding: 14, color: '#b91c1c', fontSize: 12 }}>{listError}</div>
          )}
          {!listLoading && filteredList.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#999', fontSize: 13 }}>
              <IcEmpty size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div>Nothing in the outbox</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Contacts queued for sending will appear here</div>
            </div>
          )}
          {filteredList.map(r => {
            const isSelected = r.id === selectedId;
            const meta = STATUS_META[r.status] || { label: r.status, colour: '#666', bg: '#f4f4f5', Icon: IcClock };
            return (
              <div key={r.id} onClick={() => setSelectedId(r.id)}
                style={{
                  padding: '12px 14px', borderBottom: '1px solid #ececec', cursor: 'pointer',
                  background: isSelected ? '#fff' : 'transparent', borderLeft: isSelected ? '3px solid #8B1A1A' : '3px solid transparent',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <div style={{ fontWeight: 500, fontSize: 13, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>
                    {r.full_name || r.email}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>{formatWhen(r.next_send_at)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                  {r.company_name || r.email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '1px 6px', borderRadius: 3, background: meta.bg, color: meta.colour, fontWeight: 500 }}>
                    <meta.Icon size={10} />{meta.label}
                  </span>
                  {r.campaign_name && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#ececec', color: '#555' }}>
                      {r.campaign_name}{r.current_step != null ? ` · step ${r.current_step + 1}` : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANE */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#fafafa' }}>
        {!selected && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
            Select a queued contact to view
          </div>
        )}
        {selected && (() => {
          const meta = STATUS_META[selected.status] || { label: selected.status, colour: '#666', bg: '#f4f4f5', Icon: IcClock };
          return (
            <>
              <div style={{ padding: 20, borderBottom: '1px solid #e5e5e5', background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: 17, fontWeight: 600 }}>{selected.full_name || selected.email}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#666', flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IcMail size={12} />{selected.email}</span>
                      {selected.job_title && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IcUser size={12} />{selected.job_title}</span>}
                      {selected.company_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IcCompany size={12} />{selected.company_name}</span>}
                    </div>
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, background: meta.bg, color: meta.colour, fontSize: 12, fontWeight: 500, flexShrink: 0 }}>
                    <meta.Icon size={12} />{meta.label}
                  </span>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  {(selected.status === 'paused') && (
                    <button onClick={() => doAction('resume')} disabled={actionBusy === 'resume'}
                      style={actionBtnStyle('#1a7f3c', actionBusy === 'resume')}>
                      <IcPlay size={13} />{actionBusy === 'resume' ? 'Resuming…' : 'Resume'}
                    </button>
                  )}
                  {(selected.status === 'pending' || selected.status === 'in_progress') && (
                    <button onClick={() => doAction('pause')} disabled={actionBusy === 'pause'}
                      style={actionBtnStyle('#a16207', actionBusy === 'pause')}>
                      <IcPause size={13} />{actionBusy === 'pause' ? 'Pausing…' : 'Pause'}
                    </button>
                  )}
                  <button onClick={() => doAction('send-now')} disabled={actionBusy === 'send-now'}
                    style={actionBtnStyle('#8B1A1A', actionBusy === 'send-now')}>
                    <IcZap size={13} />{actionBusy === 'send-now' ? 'Sending…' : 'Send now'}
                  </button>
                  <button onClick={() => doAction('skip-step')} disabled={actionBusy === 'skip-step'}
                    style={actionBtnStyle('#0e7490', actionBusy === 'skip-step', true)}>
                    <IcSkip size={13} />{actionBusy === 'skip-step' ? 'Skipping…' : 'Skip step'}
                  </button>
                  <button onClick={() => doAction('remove')} disabled={actionBusy === 'remove'}
                    style={actionBtnStyle('#b91c1c', actionBusy === 'remove', true)}>
                    <IcRemove size={13} />{actionBusy === 'remove' ? 'Removing…' : 'Remove from campaign'}
                  </button>
                </div>
                {actionError && (
                  <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 12 }}>{actionError}</div>
                )}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 14, fontSize: 13, lineHeight: 1.6, background: '#fff', padding: 18, borderRadius: 6, border: '1px solid #e5e5e5' }}>
                  <div style={{ color: '#888', fontWeight: 500 }}>Campaign</div>
                  <div>{selected.campaign_name || '—'}</div>

                  <div style={{ color: '#888', fontWeight: 500 }}>Current step</div>
                  <div>{selected.current_step != null ? `Step ${selected.current_step + 1}` : '—'}</div>

                  <div style={{ color: '#888', fontWeight: 500 }}>Next send</div>
                  <div>{formatFullWhen(selected.next_send_at)} {selected.next_send_at && <span style={{ color: '#888', marginLeft: 6 }}>({formatWhen(selected.next_send_at)})</span>}</div>

                  <div style={{ color: '#888', fontWeight: 500 }}>Status</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 3, background: meta.bg, color: meta.colour, fontSize: 12, fontWeight: 500 }}>
                      <meta.Icon size={11} />{meta.label}
                    </span>
                  </div>

                  {selected.last_event && (
                    <>
                      <div style={{ color: '#888', fontWeight: 500 }}>Last event</div>
                      <div>{selected.last_event} {selected.last_event_at && <span style={{ color: '#888', marginLeft: 6 }}>· {formatWhen(selected.last_event_at)}</span>}</div>
                    </>
                  )}

                  {selected.retry_count > 0 && (
                    <>
                      <div style={{ color: '#888', fontWeight: 500 }}>Retries</div>
                      <div>{selected.retry_count}</div>
                    </>
                  )}

                  {selected.client && (
                    <>
                      <div style={{ color: '#888', fontWeight: 500 }}>Client</div>
                      <div>{selected.client.name}</div>
                    </>
                  )}
                </div>

                {/* Email preview */}
                <div style={{ marginTop: 18, background: '#fff', borderRadius: 6, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #ececec', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Email preview</div>
                    <div style={{ fontSize: 11, color: '#888' }}>Read-only. Edit on the Sequences screen.</div>
                  </div>
                  {sequenceLoading && (
                    <div style={{ padding: 20, color: '#999', fontSize: 13, textAlign: 'center' }}>Loading sequence…</div>
                  )}
                  {sequenceError && (
                    <div style={{ padding: 16, color: '#b91c1c', fontSize: 12 }}>{sequenceError}</div>
                  )}
                  {!sequenceLoading && !sequenceError && (!sequenceContent || !Array.isArray(sequenceContent.content) || sequenceContent.content.length === 0) && (
                    <div style={{ padding: 20, color: '#999', fontSize: 13, textAlign: 'center' }}>No email sequence found for this contact.</div>
                  )}
                  {!sequenceLoading && !sequenceError && sequenceContent && Array.isArray(sequenceContent.content) && sequenceContent.content.length > 0 && (
                    <div>
                      {sequenceContent.content.map((step, idx) => {
                        const stepNum = step.step != null ? step.step : (idx + 1);
                        const isCurrent = selected.current_step != null && stepNum === (selected.current_step + 1);
                        return (
                          <div key={idx} style={{
                            padding: '14px 18px',
                            borderBottom: idx < sequenceContent.content.length - 1 ? '1px solid #ececec' : 'none',
                            background: isCurrent ? '#fffaf0' : '#fff',
                            borderLeft: isCurrent ? '3px solid #8B1A1A' : '3px solid transparent',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: isCurrent ? '#8B1A1A' : '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Step {stepNum}{isCurrent && ' · next to send'}
                                </div>
                                {projectedDates[stepNum] && (() => {
                                  const isPast = selected.current_step != null && stepNum < (selected.current_step + 1);
                                  const isHypo = projectedDates._hypothetical;
                                  let label = formatStepDate(projectedDates[stepNum], isPast, isCurrent);
                                  if (isHypo && label && !isPast) label = 'if resumed: ' + label.replace(/^(Sends|~ )/, '').trim();
                                  return label ? (
                                    <span style={{ fontSize: 11, color: isCurrent ? '#8B1A1A' : (isPast ? '#888' : '#666'), fontWeight: 500, fontStyle: isHypo ? 'italic' : 'normal' }}>· {label}</span>
                                  ) : null;
                                })()}
                              </div>
                              {sequenceContent.approval_status && (
                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: sequenceContent.approval_status === 'approved' ? '#dcfce7' : '#fef3c7', color: sequenceContent.approval_status === 'approved' ? '#15803d' : '#a16207', fontWeight: 500 }}>
                                  {sequenceContent.approval_status}
                                </span>
                              )}
                            </div>
                            {step.subject && (
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#222', marginBottom: 8 }}>{step.subject}</div>
                            )}
                            {step.body && (
                              <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{step.body}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
